import { createContext, useContext, useState, useEffect } from 'react';
import { omniLogger } from '../utils/omniLogger';
import { getCachedStorageItem, setCachedStorageItem, removeCachedStorageItem } from '../utils/storageCache';
import { rememberLastAccount } from '../utils/lastAccount';
import type { ReactNode } from 'react';
import { setElectronToken, apiFetch } from '../utils/api';
import { track } from '../utils/telemetry';
import { getDefaultAppRoute, setPostLoginRedirect } from '../utils/profiles';

// Sotto Electron (file://) gli URL root-relative (/api/...) non hanno origin:
// vanno instradati al backend assoluto.
const IS_ELECTRON = typeof window !== 'undefined' && window.location.protocol === 'file:';
// Il risolutore di URL locale e' stato rimosso: `apiFetch` (utils/api.ts) e' l'unica strada per
// il backend e ora onora anche `local_api_override`, che prima valeva per la sola chiamata Google.

interface User {
  id: string;
  email: string;
  name: string;
  avatar: string;
  role: 'admin' | 'premium' | 'free';
}

// Single dev-only admin session, shared by the local login bypass and the
// __ANTIGRAVITY_LOGIN hook the e2e tests rely on. No auth backend runs on
// :8787 in dev, so the user is hydrated straight from localStorage.
// REQ-014: identita' fittizia `.local` (come gli script e2e): l'email del titolare non sta nel bundle.
const DEV_ADMIN_USER: User = {
  id: 'dev-admin',
  email: 'dev-admin@jarvis.local',
  name: 'Admin',
  avatar: '',
  role: 'admin',
};

function seedDevAdminSession(redirectPath = getDefaultAppRoute()) {
  setCachedStorageItem('token', 'dev-admin-token');
  setCachedStorageItem('di_user', JSON.stringify(DEV_ADMIN_USER));
  // dual-write chiavi canoniche Genoma (modulo auth unificato)
  localStorage.setItem('genoma_token', 'dev-admin-token');
  localStorage.setItem('genoma_user', JSON.stringify(DEV_ADMIN_USER));
  // NIENTE genoma_last_user qui: il mock dev non deve avvelenare l'identita'
  // ricordata (foto/nome reali arrivano solo dal login Google vero).
  redirectAfterSeed(redirectPath);
}



function redirectAfterSeed(redirectPath: string) {
  /* v8 ignore start -- Electron hash routing, tested in E2E */
  if (typeof window !== 'undefined' && window.location.protocol === 'file:') {
    window.location.hash = `#/it${redirectPath}`;
    window.location.reload();
  } else {
    window.location.href = redirectPath;
  }
  /* v8 ignore end */
}

// Ritorna true se il reload cieco deve partire: quando il DesktopAuthGate e'
// montato (flag window.__jarvisAuthGateActive) il gate orchestra SUCCESS+reload.
// eslint-disable-next-line react-refresh/only-export-components -- helper puro co-locato col provider
export function shouldReloadOnAuthSuccess(): boolean {
  return !window.__jarvisAuthGateActive;
}

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  isPremium: boolean;
  isAuthenticating: boolean;
  /** true solo in Electron prod quando non c'è sessione reale: forza la schermata di login */
  needsElectronLogin: boolean;
  login: (provider: 'google' | 'apple', redirectPath?: string) => void;
  logout: () => void;
  handleGoogleCredential: (credential: string, redirectPath?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [needsElectronLogin, setNeedsElectronLogin] = useState(false);

  // Assicuriamo che esista un guest_session_id
  useEffect(() => {
    if (!getCachedStorageItem('guest_session_id')) {
      setCachedStorageItem('guest_session_id', crypto.randomUUID());
    }
    
    // Dev-only admin login bypass, also exposed for e2e tests
    if (import.meta.env.DEV) {
      (window as { __ANTIGRAVITY_LOGIN?: () => void }).__ANTIGRAVITY_LOGIN = () => seedDevAdminSession();
    }
  }, []);

  useEffect(() => {
    const storedUser = getCachedStorageItem('di_user');
    if (storedUser) {
      try {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- rehydrate da storage al mount
        setUser(JSON.parse(storedUser));
      } catch {
        removeCachedStorageItem('di_user');
      }
    }

    const hydrateFromToken = async () => {
      setIsAuthenticating(true);
      let currentToken: string;

      if (IS_ELECTRON && window.electronAPI?.getToken) {
        // Idratazione sicura da safeStorage (niente localStorage)
        currentToken = await window.electronAPI.getToken() || '';
        if (currentToken) {
          setElectronToken(currentToken);
        }
      } else {
        currentToken = getCachedStorageItem('token') || '';
      }

      // Bonifica installazioni Electron esistenti: le vecchie build potevano
      // seminare token mock persistiti. In una build pacchettizzata nessun
      // flag localStorage puo' esentare questo controllo: sarebbe aggirabile
      // dall'utente e porterebbe a richieste 401 mascherate da errori AI/STT.
      const lsToken = getCachedStorageItem('token');
      const hasMockToken =
        currentToken === 'dev-admin-token' || currentToken === 'mock-token' ||
        (!IS_ELECTRON && (lsToken === 'dev-admin-token' || lsToken === 'mock-token'));

      if (IS_ELECTRON && !import.meta.env.DEV &&
          (!currentToken || hasMockToken)) {
        // TOKEN ASSENTE O MOCK in Electron prod → NON operare in guest silenzioso.
        // Pulizia totale dei token sporchi e redirect al login.
        removeCachedStorageItem('token');
        removeCachedStorageItem('di_user');
        removeCachedStorageItem('di_e2e_mock');
        removeCachedStorageItem('local_api_override');
        setElectronToken(null);
        setUser(null);
        setNeedsElectronLogin(true);
        setIsAuthenticating(false);
        omniLogger.warn('AuthContext', '[Electron] Nessuna sessione reale trovata — needsElectronLogin=true');
        return;
      }

      // Se non abbiamo un token e siamo in Prod, fine idratazione (mostrerà login)
      if (!currentToken && !import.meta.env.DEV) {
        setIsAuthenticating(false);
        return;
      }

      // Solo il bundle Vite di sviluppo puo' generare una sessione mock.
      // Un marker localStorage non e' una prova affidabile del runtime di test.
      if (import.meta.env.DEV) {
        if (!currentToken) {
           omniLogger.info('AuthContext', '[DEV] Token assente. Generazione auto sessione mock dev-admin.');
           setCachedStorageItem('token', 'dev-admin-token');
           setCachedStorageItem('di_user', JSON.stringify(DEV_ADMIN_USER));
           setUser(DEV_ADMIN_USER);
        }
        setIsAuthenticating(false);
        return;
      }

      /* v8 ignore start -- production-only validation, tested in E2E */
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // 8-second timeout

        const res = await apiFetch('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${currentToken}`,
            'x-app-version': typeof __APP_VERSION_RAW__ !== 'undefined' ? __APP_VERSION_RAW__ : ''
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem('di_user');
            if (IS_ELECTRON && window.electronAPI?.logout) {
              window.electronAPI.logout();
              // T7: in Electron il login e' il DesktopAuthGate su '#/', NON la
              // pagina web '/it/login' (marketing). Altrimenti dopo un 401
              // l'app resta bloccata sulla pagina web Auth (regressione landing).
              window.location.hash = '#/';
              window.location.reload();
              return;
            }
            window.location.href = '/it/auth-error';
            return;
          }
          setIsAuthenticating(false);
          return;
        }

        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          omniLogger.warn('AuthContext', 'Expected JSON from /api/auth/me but got HTML/text. Possible Cloudflare block.');
          localStorage.removeItem('token');
          localStorage.removeItem('di_user');
          setIsAuthenticating(false);
          return;
        }

        const data = await res.json();
        // REQ-014: il ruolo lo decide solo il server (nessuna email proprietario nel bundle).
        const role = (data?.role === 'admin' || data?.role === 'premium' || data?.role === 'free') ? data.role : 'free';
        const nextUser: User = {
          id: String(data?.email || 'usr'),
          email: String(data?.email || ''),
          name: String(data?.name || data?.email || 'User'),
          avatar: String(data?.avatar || ''),
          role,
        };
        setUser(nextUser);
        if (window.electronAPI?.sendUserId && nextUser.id) {
          window.electronAPI.sendUserId(nextUser.id).catch(() => {});
        }
        localStorage.setItem('di_user', JSON.stringify(nextUser));
        // dual-write chiavi canoniche Genoma (modulo auth unificato)
        localStorage.setItem('genoma_token', currentToken);
        localStorage.setItem('genoma_user', JSON.stringify(nextUser));
        localStorage.setItem('genoma_last_user', JSON.stringify(nextUser));
        // Alimenta il bottone "Continua come ..." (sopravvive al logout, pattern Gmail)
        rememberLastAccount(nextUser, 'jarvis:last-account');

        if (data.isNewUser) {
          localStorage.setItem('has_seen_welcome_modal', 'false');
        }

      } catch {
         // Silently catch in dev, normally report
      } finally {
        setIsAuthenticating(false);
      }
      /* v8 ignore end */
    };

    hydrateFromToken();
  }, []);

  // Electron: riceve l'evento auth-success (il token è in safeStorage)
  useEffect(() => {
    const electronApi = typeof window !== 'undefined' ? window.electronAPI : undefined;
    if (!electronApi?.onAuthSuccess) return;
    return electronApi.onAuthSuccess(() => {
      if (!shouldReloadOnAuthSuccess()) return;
      window.location.reload();
    });
  }, []);

  // A3: refresh silenzioso del JWT (no reload, aggiorna solo la cache)
  useEffect(() => {
    const electronApi = typeof window !== 'undefined' ? window.electronAPI : undefined;
    if (!electronApi?.onTokenRefreshed) return;
    return electronApi.onTokenRefreshed(async () => {
      if (electronApi?.getToken) {
        const newToken = await electronApi.getToken();
        if (newToken) {
          setElectronToken(newToken);
        }
      }
    });
  }, []);

  const login = (provider: 'google' | 'apple', redirectPath: string = getDefaultAppRoute()) => {
    void provider;
    // Solo in DEV il login bypassa OAuth con la sessione admin mock locale.
    if (import.meta.env.DEV) {
      seedDevAdminSession(redirectPath);
      track('AUTH', { action: 'login_success', provider: 'google' });
      return;
    }
    // Electron pacchettizzato: avvia il flusso PKCE via IPC (nessun browser redirect)
    if (IS_ELECTRON) {
      if (window.electronAPI?.login) {
        window.electronAPI.login();
      }
      return;
    }
    // Web/produzione: Google GIS (GoogleSignInButton gestisce il credential,
    // handleGoogleCredential lo invia al backend). Fallback navigazione login.
    // Persiste il target del CTA per atterrare sulla pagina voluta dopo l'auth.
    setPostLoginRedirect(redirectPath);
    window.location.href = '/it/login';
  };

  const handleGoogleCredential = async (credential: string, redirectPath: string = getDefaultAppRoute()) => {
    const res = await apiFetch('/api/auth/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error || `HTTP_${res.status}`);
    }
    const data = await res.json();
    const role = data.user.role === 'admin' ? 'admin' : data.user.role === 'premium' ? 'premium' : 'free';
    const sessionUser: User = {
      id: data.user.email,
      email: data.user.email,
      name: data.user.name,
      avatar: data.user.avatar || '',
      role,
    };
    setCachedStorageItem('token', data.token);
    setCachedStorageItem('di_user', JSON.stringify(sessionUser));
    localStorage.setItem('genoma_token', data.token);
    localStorage.setItem('genoma_user', JSON.stringify(sessionUser));
    localStorage.setItem('genoma_last_user', JSON.stringify(sessionUser));
    rememberLastAccount({ email: sessionUser.email, name: sessionUser.name, avatar: sessionUser.avatar }, 'jarvis:last-account');
    setUser(sessionUser);
    track('AUTH', { action: 'login_success', provider: 'google' });
    window.location.href = redirectPath;
  };

  const logout = () => {
    setUser(null);
    removeCachedStorageItem('di_user');
    removeCachedStorageItem('token');
    localStorage.removeItem('genoma_token');
    localStorage.removeItem('genoma_user');
    /* v8 ignore start -- production/Electron logout, tested in E2E */
    if (!import.meta.env.DEV && !IS_ELECTRON) {
      window.location.href = '/it/login';
    } else if (IS_ELECTRON) {
      window.electronAPI?.logout?.();
      // T7: come sopra — il gate di login vive su '#/', non sulla pagina web.
      window.location.hash = '#/';
      window.location.reload();
    }
    /* v8 ignore end */
  };

  // Il ruolo viene determinato esclusivamente dal backend.
  // REQ-014: nessuna email proprietario nel bundle; REQ-015: `premium` non si degrada.
  const isAdmin = user?.role === 'admin';
  const isPremium = isAdmin || user?.role === 'premium';

  return (
    <AuthContext.Provider value={{ user, isAdmin, isPremium, isAuthenticating, needsElectronLogin, login, logout, handleGoogleCredential }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- hook di contesto co-locato col provider (idioma standard)
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
